import { prepareCompactLeagueRows } from "./compactLeagueRows.js";
export const overviewMetrics = [
  "total_strength",
  "total_goals",
  "for_homefactor",
  "against_homefactor",
];
// Adapter for betty's existing venue-balanced table derivation. Unit exposure
// converts rates back to rates; these are not invented observed match counts.
export function deriveOverview(values = {}) {
  const valid = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0;
  const scope = (venue) => ({
    games_played: 1,
    goals_scored: valid(values["for_" + venue])
      ? values["for_" + venue]
      : undefined,
    goals_against: valid(values["against_" + venue])
      ? values["against_" + venue]
      : undefined,
  });
  const row = prepareCompactLeagueRows(
    [{ team_id: 0, home: scope("home"), away: scope("away") }],
    "actual"
  )[0].fixtureSettingValues;
  return Object.fromEntries(
    overviewMetrics.map((k) => [k, Number.isFinite(row[k]) ? row[k] : null])
  );
}
export function teamOverview(team, draft) {
  const sources = Object.fromEntries(
    [
      ["saved", team.setting],
      ["actual", team.references?.actual?.fixtureSettingValues],
      ["expected", team.references?.expected?.fixtureSettingValues],
      ["should", team.references?.should?.fixtureSettingValues],
      ["market", team.references?.market?.fixtureSettingValues],
      ["previous", team.references?.previous?.fixtureSettingValues],
      ["previous_saved", team.references?.previous_saved?.fixtureSettingValues],
      ["draft", draft],
    ].map(([key, values]) => [key, deriveOverview(values)])
  );
  const relativeSources = Object.fromEntries(
    ["actual", "should", "market"].map((name) => {
      const core = {};
      for (const field of [
        "for_home",
        "against_home",
        "for_away",
        "against_away",
      ]) {
        const matched =
          name === "market"
            ? team.market_comparison?.[field]
            : team.performance_comparison?.[field]?.[
                name === "actual" ? "real" : "should"
              ];
        const venue = field.endsWith("_home") ? "home" : "away";
        const ref = team.references?.[name],
          exp = team.references?.expected;
        const equalSample =
          name !== "market" &&
          !team.performance_comparison &&
          ref?.[venue]?.played > 0 &&
          ref[venue].played === exp?.[venue]?.played;
        const value = matched
          ? name === "market"
            ? matched.market
            : matched.value
          : equalSample
          ? ref?.fixtureSettingValues?.[field]
          : null;
        const expected = matched
          ? matched.expected
          : equalSample
          ? exp?.fixtureSettingValues?.[field]
          : null;
        core[field] =
          Number.isFinite(value) && value >= 0 && expected > 0
            ? (team.setting[field] * value) / expected
            : null;
      }
      return [name, deriveOverview(core)];
    })
  );
  const diagnostics = [];
  // Diagnostic thresholds only: two current sources with >=5% same-direction
  // movement; flag an opposite draft or <25% of their median relative movement.
  for (const metric of overviewMetrics) {
    const base = sources.saved[metric],
      next = sources.draft[metric];
    if (!(base > 0) || next === null) continue;
    const changes = ["actual", "should", "market"]
      .filter((k) => relativeSources[k][metric] !== null)
      .map((source) => ({
        source,
        delta: relativeSources[source][metric] / base - 1,
      }));
    for (const direction of [-1, 1]) {
      const agreeing = changes.filter((r) => direction * r.delta >= 0.05);
      if (agreeing.length < 2) continue;
      const sorted = agreeing
        .map((r) => Math.abs(r.delta))
        .sort((a, b) => a - b);
      const middle =
        (sorted[Math.floor((sorted.length - 1) / 2)] +
          sorted[Math.ceil((sorted.length - 1) / 2)]) /
        2;
      const movement = direction * (next / base - 1);
      if (movement < 0.25 * middle)
        diagnostics.push({
          metric,
          code: movement < 0 ? "opposite_direction" : "weak_response",
          sources: agreeing,
          source_median_change: middle,
          draft_relative_change: next / base - 1,
        });
    }
  }
  return {
    sources,
    relative_sources: relativeSources,
    diagnostics,
    diagnostic_rule: {
      minimum_sources: 2,
      minimum_relative_source_change: 0.05,
      minimum_response_fraction: 0.25,
    },
    source_note:
      "Raw source values are shown separately. Direction diagnostics use setting-relative targets against matched Expected; missing aligned evidence is excluded. No setting correction.",
  };
}
